import * as React from 'react';
import styled from 'styled-components/macro';
import { Field, FieldProps, ErrorMessage, useFormikContext } from 'formik';
import { SelectInput, Spinner } from 'lib/components';
import { Button } from 'react-covideo-common';
import { theme } from 'lib/style';
import { keyBy } from 'lodash';
import { useState } from 'react';
import { getGraphAuthUrl } from 'lib/api';
import { errorToast } from 'lib/components/toasts/error';
import { MdOpenInNew } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { UserValues } from './userValidator';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: flex-start;
  width: 100%;
  padding: 0.5rem 0;
  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
  button {
    margin-left: 102px;
  }
`;

const FieldLabel = styled.label`
  width: 120px;
  margin-right: 8px;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray60};
`;

const SectionTitle = styled.p`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  margin: 0;
  width: 100%;
  max-width: 200px;
  line-height: 24px;
`;

const RowWrapper = styled.div`
  width: 100%;
  div:first-child,
  p:first-child {
    padding-top: 0px;
  }
`;
interface TextInputProps {
  margin?: string;
  background?: string | null;
}

const FieldContainer = styled.section`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const TextInput = styled.input.attrs(({ type }) => ({
  type: type || 'text',
}))<TextInputProps>`
  display: block;
  width: 100%;
  max-width: 800px;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  background-color: ${props => props.background || '#fff'};
  background-clip: padding-box;
  border: 1px solid #ced4da;
  color: #495057;
  border-radius: 0.25rem;
  box-sizing: border-box;
  margin: ${props => props.margin || ''};
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const ErrorMsg = styled.p`
  color: red;
  font-size: 14px;
  padding: 0.1rem;
`;

export const CalendarPlatform = {
  NOT_SELECTED: {
    value: 0,
    label: 'Not selected',
  },
  GOOGLE: {
    value: 1,
    label: 'Google Calendar',
  },
  CALENDLY: {
    value: 2,
    label: 'Calendly',
  },
  OUTLOOK: {
    value: 3,
    label: 'Outlook Calendar',
  },
  WEBSITE: {
    value: 4,
    label: 'Other',
  },
};

export const IntegrationsForm = () => {
  const { isSubmitting } = useFormikContext<UserValues>();
  const { t } = useTranslation();
  const [platform, setPlatform] = useState<number | undefined>();
  const [authenticating, setAuthenticating] = useState(false);
  const authenticateWithOutlook = async () => {
    setAuthenticating(true);
    const res = await getGraphAuthUrl();
    if (res) {
      window.location.href = res;
    } else {
      errorToast({ title: t('There was a problem with authentication.') });
      setAuthenticating(false);
    }
  };

  const translatedPlatformOptions = Object.values(
    keyBy(CalendarPlatform, 'value')
  ).map(o => ({
    value: o.value,
    label: t(o.label),
  }));

  return (
    <>
      <div style={{ display: 'flex', maxWidth: 700 }}>
        <SectionTitle>{t('Scheduling Integrations')}</SectionTitle>
        <RowWrapper>
          <Row>
            <FieldLabel>{t('Platform')}</FieldLabel>
            <Field name='platform'>
              {/*    {TO DO REPLACE WITH FORMIKDROPDOWN} */}
              {({ field }: FieldProps) => {
                const { value, ...rest } = field;
                setPlatform(value);
                return (
                  <SelectInput
                    {...rest}
                    defaultValue={value}
                    disabled={isSubmitting}
                  >
                    {translatedPlatformOptions.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </SelectInput>
                );
              }}
            </Field>
          </Row>
          {platform == CalendarPlatform.GOOGLE.value && (
            <Row>
              <FieldLabel>{t('Calendar ID')}</FieldLabel>
              <Field name='googleCalendarId'>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <TextInput
                        {...field}
                        type='text'
                        placeholder={t('Calendar ID')}
                        required={true}
                        disabled={isSubmitting}
                      />
                      <ErrorMessage
                        name='googleCalendarId'
                        component={ErrorMsg}
                      />
                    </FieldContainer>
                  );
                }}
              </Field>
            </Row>
          )}
          {platform == CalendarPlatform.CALENDLY.value && (
            <>
              <Row>
                <FieldLabel>{t('Calendar Link')}</FieldLabel>
                <Field name='calendlyCalendarUrl'>
                  {({ field }: FieldProps) => {
                    return (
                      <FieldContainer>
                        <TextInput
                          {...field}
                          type='text'
                          placeholder={t('Calendar Link')}
                          required={true}
                          disabled={isSubmitting}
                        />
                        <ErrorMessage
                          name='calendlyCalendarUrl'
                          component={ErrorMsg}
                        />
                      </FieldContainer>
                    );
                  }}
                </Field>
              </Row>
              <Row>
                <FieldLabel>{t('Access Token')}</FieldLabel>
                <Field name='calendlyAccessToken'>
                  {({ field }: FieldProps) => {
                    return (
                      <FieldContainer>
                        <TextInput
                          {...field}
                          type='text'
                          placeholder={t('Access Token')}
                          required={true}
                          disabled={isSubmitting}
                        />
                        <ErrorMessage
                          name='calendlyAccessToken'
                          component={ErrorMsg}
                        />
                      </FieldContainer>
                    );
                  }}
                </Field>
              </Row>
            </>
          )}
          {platform == CalendarPlatform.OUTLOOK.value && (
            <>
              <Row>
                <Button
                  text={t('Authenticate with Outlook')}
                  onClick={() => authenticateWithOutlook()}
                  variant='primary'
                  disabled={authenticating}
                  icon={
                    authenticating ? (
                      <Spinner />
                    ) : (
                      <MdOpenInNew size='14px' color={theme.palette.white} />
                    )
                  }
                />
              </Row>
              <Row>
                <FieldLabel>{t('Access Token')}</FieldLabel>
                <Field name='outlookRefreshToken'>
                  {({ field }: FieldProps) => {
                    return (
                      <FieldContainer>
                        <TextInput
                          {...field}
                          type={'text'}
                          placeholder={t('Access Token')}
                          required={true}
                          disabled={true}
                        />
                        <ErrorMessage
                          name='outlookRefreshToken'
                          component={ErrorMsg}
                        />
                      </FieldContainer>
                    );
                  }}
                </Field>
              </Row>
            </>
          )}
          {platform == CalendarPlatform.WEBSITE.value && (
            <Row>
              <FieldLabel>{t('Website Link')}</FieldLabel>
              <Field name='calendarWebsiteUrl'>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <TextInput
                        {...field}
                        type='text'
                        placeholder={t('Website Link')}
                        required={true}
                        disabled={isSubmitting}
                      />
                      <ErrorMessage
                        name='calendarWebsiteUrl'
                        component={ErrorMsg}
                      />
                    </FieldContainer>
                  );
                }}
              </Field>
            </Row>
          )}
        </RowWrapper>
      </div>
    </>
  );
};
